import { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';
import { serializeError } from '@/store/utils/serialize-error';

import { SpeakersState } from './types';
import upsertSpeakerById from './helpers/upsert-speaker-by-id';
import { RootState } from '@/store/main';
import {
  FetchSpeakersPage,
  FetchSpeakersByIds,
  UpdateSpeaker,
  SPEAKERS_PAGE_SIZE,
} from '@/connection/speakers/speakers';
import { Speaker } from '@/types/speakers.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';

export const reducers = (create: ReducerCreators<SpeakersState>) => ({
  fetchSpeakersPage: create.asyncThunk(
    async ({
      channel,
      page = 1,
      search,
      limit = SPEAKERS_PAGE_SIZE,
    }: {
      channel: number;
      page?: number;
      search?: string;
      limit?: number;
    }) => {
      const response = await FetchSpeakersPage({ channel, page, search, limit });
      return { ...response, page, search: search || '' };
    },
    {
      pending: (state, action) => {
        const { search, page = 1 } = action.meta.arg;
        const list = search ? state.searchedSpeakers : state.speakers;
        list.loading = true;
        if (page <= 1) {
          list.ids = [];
          list.page = 0;
          list.total = null;
        }
      },
      fulfilled: (state, action) => {
        const { results, total, page, search } = action.payload;
        results.forEach((speaker) => upsertSpeakerById(speaker, state));

        const list = search ? state.searchedSpeakers : state.speakers;
        const ids = results.map((speaker) => speaker.speaker);

        const seen = new Set(list.ids);
        list.ids = page <= 1 ? ids : [...list.ids, ...ids.filter((id) => !seen.has(id))];
        list.page = page;
        list.total = total;
        list.loading = false;
      },
      rejected: (state, action) => {
        const list = action.meta.arg.search ? state.searchedSpeakers : state.speakers;
        list.loading = false;
        console.error(action.error);
      },
    }
  ),

  fetchSpeakersByIds: create.asyncThunk(
    async ({ channel, speakerIds }: { channel: number; speakerIds: number[] }, { getState }) => {
      const { speakersById } = (getState() as RootState).speakers;
      const missing = speakerIds.filter((id) => speakersById[id] === undefined);

      if (!missing.length) return [] as Speaker[];

      return FetchSpeakersByIds({ channel, speakerIds: missing });
    },
    {
      pending: (state, action) => {
        action.meta.arg.speakerIds.forEach((id) => {
          if (state.speakersById[id] === undefined) state.speakersById[id] = null;
        });
      },
      fulfilled: (state, action) => {
        action.payload.forEach((speaker) => upsertSpeakerById(speaker, state));
      },
      rejected: (state, action) => {
        action.meta.arg.speakerIds.forEach((id) => {
          if (state.speakersById[id] === null) delete state.speakersById[id];
        });
        console.error(action.error);
      },
    }
  ),

  updateSpeaker: create.asyncThunk(
    async (
      {
        speaker,
        baseLanguage,
        languages,
      }: {
        /** originalSpeaker used to reset speakers on the project sessions should the api call fail */
        originalSpeaker: Speaker;
        speaker: Speaker;
        baseLanguage: string;
        languages: string[];
      },
      { rejectWithValue }
    ) => {
      try {
        return UpdateSpeaker({ speaker, baseLanguage, languages });
      } catch (error) {
        return rejectWithValue(serializeError(error));
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) upsertSpeakerById(action.payload, state);
      },
      rejected: (_state, action) => {
        console.error(action.error);
        toastTrigger('There was an error updating your cast', {
          type: EToastTypes.error,
          description: 'Please refresh and try again',
        });
      },
    }
  ),
  upsertSpeaker: create.reducer((state, action: PayloadAction<Speaker>) => {
    upsertSpeakerById(action.payload, state);
  }),
});
